import {
  BOT_LEVELS,
  EVENTS,
  MAX_PLAYERS,
  programmeName,
  type PlayerInfo,
  type RoomInfo,
  type Programme,
  type RoomOptions,
} from '@decathlon/shared';
import { clear, EVENT_EMOJI, h } from '../dom.js';
import { PLAYABLE, PLAYABLE_PROGRAMMES } from './menu.js';

export interface LobbyModel {
  status: string;
  myId: string | null;
  players: PlayerInfo[];
  options: RoomOptions;
  online: boolean;
  room?: RoomInfo | null;
  inviteLink?: string | undefined;
}

export interface LobbyActions {
  ready(r: boolean): void;
  start(): void;
  setOptions(o: Partial<RoomOptions>): void;
  leave(): void;
  showLeague(): void;
}

/** Programmes on offer: whole days only once every event in them is playable. */
const PROGRAMMES: Programme[] = [
  ...(['full', 'day1', 'day2'] as Programme[]).filter((p) => PLAYABLE_PROGRAMMES.has(p)),
  ...EVENTS.filter((e) => PLAYABLE.has(e.id)).map((e) => e.id),
];

export function renderLobby(root: HTMLElement, m: LobbyModel, a: LobbyActions): void {
  clear(root);
  const me = m.players.find((p) => p.id === m.myId);
  const isHost = !!me?.isHost;
  const humans = m.players.filter((p) => !p.isBot);
  const allReady = humans.length > 0 && humans.every((p) => p.ready || p.isHost);
  const seats: (PlayerInfo | null)[] = [...m.players];
  while (seats.length < MAX_PLAYERS) seats.push(null);

  const shareStatus = h('p', { class: 'muted small', role: 'status' });
  const inviteInput = h('input', {
    type: 'text',
    value: m.inviteLink ?? '',
    readonly: true,
    'aria-label': 'Invite link',
  });
  const copyInvite = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(m.inviteLink!);
      shareStatus.textContent = 'Invite link copied.';
    } catch {
      inviteInput.focus();
      inviteInput.select();
      shareStatus.textContent = 'Select and copy the invite link above.';
    }
  };
  root.append(
    h(
      'div',
      { class: 'center' },
      h(
        'div',
        { class: 'lobby' },
        h(
          'div',
          { class: 'panel stack' },
          h(
            'div',
            { class: 'row', style: 'justify-content:space-between' },
            h(
              'h1',
              {},
              m.online
                ? m.room?.isPublic
                  ? '🌐 Public room'
                  : m.room
                    ? `🔗 Room ${m.room.code}`
                    : '🌐 Online meet'
                : '🏟️ Practice meet',
            ),
            h('span', { class: 'badge' }, m.status),
          ),
          h(
            'p',
            { class: 'muted' },
            m.online
              ? 'Everyone here competes in the same meet. The host picks the programme and starts when the athletes are ready.'
              : 'You against the bots. Pick a programme and go.',
          ),
          m.online && m.room && m.inviteLink
            ? h(
                'div',
                { class: 'stack room-invite' },
                h(
                  'label',
                  {},
                  m.room.isPublic
                    ? 'Invite someone to the public room'
                    : `Share room ${m.room.code}`,
                ),
                h(
                  'div',
                  { class: 'row' },
                  inviteInput,
                  h(
                    'button',
                    { class: 'btn blue', onclick: () => void copyInvite() },
                    'Copy invite link',
                  ),
                ),
                shareStatus,
              )
            : null,
          h(
            'ul',
            { class: 'players' },
            seats.map((p) =>
              p
                ? h(
                    'li',
                    { class: p.id === m.myId ? 'me' : '' },
                    h('span', { class: 'dot', style: `background:${p.kit}` }),
                    h('span', { class: 'name' }, p.name, p.id === m.myId ? ' (you)' : ''),
                    p.isHost ? h('span', { class: 'badge' }, 'Host') : null,
                    p.isBot ? h('span', { class: 'badge bot' }, 'Bot') : null,
                    p.ready && !p.isBot ? h('span', { class: 'badge ready' }, 'Ready') : null,
                  )
                : h(
                    'li',
                    { class: 'empty-slot' },
                    m.options.fillWithBots ? 'Bot will fill this lane' : 'Empty lane',
                  ),
            ),
          ),
          h(
            'div',
            { class: 'row' },
            me && !isHost
              ? h(
                  'button',
                  {
                    class: `btn ${me.ready ? 'ghost' : 'primary'}`,
                    'aria-pressed': String(me.ready),
                    onclick: () => a.ready(!me.ready),
                  },
                  me.ready ? 'Not ready' : '✔ Ready',
                )
              : null,
            isHost
              ? h(
                  'button',
                  {
                    class: 'btn primary',
                    disabled: !allReady,
                    title: allReady ? '' : 'Waiting for everyone to be ready',
                    onclick: () => a.start(),
                  },
                  '🏁 Start meet',
                )
              : null,
            h('button', { class: 'btn ghost', onclick: () => a.showLeague() }, '🏆 League'),
            h('button', { class: 'btn ghost', onclick: () => a.leave() }, '← Leave'),
          ),
        ),
        h(
          'div',
          { class: 'panel stack' },
          h('h2', {}, 'Programme'),
          isHost ? null : h('p', { class: 'muted small' }, 'Only the host can change these.'),
          h(
            'div',
            { class: 'events' },
            PROGRAMMES.map((p) =>
              h(
                'button',
                {
                  class: `event-card${p === m.options.programme ? ' selected' : ''}`,
                  disabled: !isHost,
                  'aria-pressed': String(p === m.options.programme),
                  onclick: () => a.setOptions({ programme: p }),
                },
                h(
                  'span',
                  { class: 'emoji' },
                  p === 'full'
                    ? '🏅'
                    : p === 'day1'
                      ? '1️⃣'
                      : p === 'day2'
                        ? '2️⃣'
                        : (EVENT_EMOJI[p] ?? '🏃'),
                ),
                h('span', { class: 'ev-name' }, programmeName(p)),
              ),
            ),
          ),
          h('label', {}, 'Empty lanes'),
          h(
            'div',
            { class: 'toggle' },
            h(
              'button',
              {
                class: m.options.fillWithBots ? 'on' : '',
                disabled: !isHost,
                'aria-pressed': String(m.options.fillWithBots),
                onclick: () => a.setOptions({ fillWithBots: true }),
              },
              '🤖 Fill with bots',
            ),
            h(
              'button',
              {
                class: m.options.fillWithBots ? '' : 'on',
                disabled: !isHost,
                'aria-pressed': String(!m.options.fillWithBots),
                onclick: () => a.setOptions({ fillWithBots: false }),
              },
              'Leave empty',
            ),
          ),
          h(
            'button',
            {
              class: 'btn ghost',
              disabled: !isHost,
              'aria-pressed': String(!!m.options.strictHurdles),
              onclick: () => a.setOptions({ strictHurdles: !m.options.strictHurdles }),
            },
            m.options.strictHurdles
              ? 'Hurdles: strict (3 hits = DNF)'
              : 'Hurdles: arcade (speed penalty)',
          ),
          h('label', {}, 'Bot level'),
          h(
            'div',
            { class: 'difficulty four' },
            BOT_LEVELS.map((l) =>
              h(
                'button',
                {
                  class: `diff-btn${l.id === m.options.botLevel ? ' selected' : ''}`,
                  disabled: !isHost,
                  'aria-pressed': String(l.id === m.options.botLevel),
                  onclick: () => a.setOptions({ botLevel: l.id }),
                },
                l.name,
                h('span', {}, l.blurb),
              ),
            ),
          ),
          h(
            'p',
            { class: 'muted small' },
            'Track events run together in lanes. Field events go turn by turn: 30 seconds to start your run-up when it is your go.',
          ),
        ),
      ),
    ),
  );
}
